import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CALL_HUB_EVENTS, CALL_HUB_METHODS } from "../realtime/callEvents.js";
import { CALL_STATES, useCallSession } from "./useCallSession.js";
import {
  createGenericCallInfo,
  getPayloadCallId,
  getPayloadMessage,
  getPayloadValue,
  invokeHubClient,
  isSameCall,
  registerHubHandler,
  resolveHubConnection,
  startHubClient,
  subscribeConnectionLifecycle,
} from "./callHookUtils.js";

export const useCallEngine = ({
  hubClient,
  identity,
  receiveScopes = [],
  remoteAudioRef,
  autoRegister = true,
  mode = "receiver", // "receiver" | "participant"
  mapIncomingCall,
  onIncomingCall,
  onStateChange,
  onError,
} = {}) => {
  const connection = useMemo(() => resolveHubConnection(hubClient), [hubClient]);

  const activeCallIdRef = useRef(null);
  const registeredConnKeyRef = useRef(null);
  const seenCallIdsRef = useRef(new Set());
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [registrationState, setRegistrationState] = useState("idle");
  const [error, setError] = useState(null);

  const callbacksRef = useRef({ mapIncomingCall, onIncomingCall, onStateChange, onError });

  useEffect(() => {
    callbacksRef.current = { mapIncomingCall, onIncomingCall, onStateChange, onError };
  }, [mapIncomingCall, onError, onIncomingCall, onStateChange]);

  const handleSessionEnded = useCallback(() => {
    activeCallIdRef.current = null;
    setActiveCall(null);
    setIncomingCall(null);
  }, []);

  const callSession = useCallSession({
    connection,
    remoteAudioRef,
    onCallEnded: handleSessionEnded,
  });

  const callSessionRef = useRef(callSession);
  useEffect(() => {
    callSessionRef.current = callSession;
  }, [callSession]);

  useEffect(() => {
    callbacksRef.current.onStateChange?.(callSession.callState, callSession.callInfo);
  }, [callSession.callInfo, callSession.callState]);

  const failCall = useCallback((message, payload) => {
    const session = callSessionRef.current;
    const nextError = message || "Call service is not available.";
    setError(nextError);
    callbacksRef.current.onError?.(nextError, payload);
    session.resetCall();
    session.setCallState(CALL_STATES.idle);
    session.setCallInfo(null);
    activeCallIdRef.current = null;
    setActiveCall(null);
    setIncomingCall(null);
  }, []);

  const registerClient = useCallback(async () => {
    if (!connection || !identity) return;
    const connKey = `${connection.connectionId || "conn"}:${identity.participantId || ""}:${mode}`;
    if (registeredConnKeyRef.current === connKey) return;

    await startHubClient(hubClient);
    await callSessionRef.current.loadIceServers();
    if (mode === "participant") {
      await invokeHubClient(hubClient, connection, CALL_HUB_METHODS.registerParticipant, identity);
    } else {
      await invokeHubClient(hubClient, connection, CALL_HUB_METHODS.registerReceiver, {
        identity,
        receiveScopes,
      });
    }
    registeredConnKeyRef.current = connKey;
    setRegistrationState("registered");
  }, [connection, hubClient, identity, mode, receiveScopes]);

  const presentIncomingCall = useCallback((payload = {}, options = {}) => {
    const callId = getPayloadCallId(payload);
    if (!callId) return;

    const callIdKey = String(callId);
    const currentCallId = activeCallIdRef.current ? String(activeCallIdRef.current) : "";
    const duplicate = seenCallIdsRef.current.has(callIdKey);

    if (duplicate && currentCallId === callIdKey && callSessionRef.current.callState !== CALL_STATES.idle) {
      return;
    }

    if (duplicate && !options.allowExisting) {
      return;
    }

    const session = callSessionRef.current;
    if (session.callState !== CALL_STATES.idle) {
      invokeHubClient(hubClient, connection, CALL_HUB_METHODS.declineCall, {
        callId,
        reason: "busy",
      }).catch(() => {});
      return;
    }

    seenCallIdsRef.current.add(callIdKey);
    activeCallIdRef.current = callId;
    const nextCall =
      callbacksRef.current.mapIncomingCall?.(payload) ||
      createGenericCallInfo(payload, "Incoming call");
    setIncomingCall(nextCall);
    callbacksRef.current.onIncomingCall?.(nextCall);
    session.resetCall();
    session.setCallInfo(nextCall);
    session.setCallState(CALL_STATES.incoming);
  }, [connection, hubClient]);

  useEffect(() => {
    if (!connection || !identity) {
      setRegistrationState("idle");
      return undefined;
    }

    let mounted = true;
    if (autoRegister) {
      setRegistrationState("connecting");
      registerClient().catch((registrationError) => {
        if (!mounted) return;
        setRegistrationState("failed");
        failCall(registrationError?.message || "Unable to register call client.", registrationError);
      });
    } else {
      setRegistrationState("registered");
    }

    const cleanup = [
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.receiverRegistered, () => {
        setRegistrationState("registered");
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.participantRegistered, () => {
        setRegistrationState("registered");
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.incomingCall, presentIncomingCall),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callStarted, (payload = {}) => {
        const callId = getPayloadCallId(payload);
        if (!callId) return;
        activeCallIdRef.current = callId;
        setActiveCall((current) => ({ ...(current || {}), ...payload, callId }));
        callSessionRef.current.setCallInfo((current) => ({ ...(current || {}), ...payload, callId }));
        callSessionRef.current.setCallState(CALL_STATES.calling);
        callSessionRef.current.connectLiveKit(callId).catch(() => {});
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callQueued, (payload = {}) => {
        const callId = getPayloadCallId(payload);
        if (!callId) return;
        activeCallIdRef.current = callId;
        setActiveCall((current) => ({ ...(current || {}), ...payload, callId }));
        callSessionRef.current.setCallInfo((current) => ({
          ...(current || {}),
          ...payload,
          callId,
          queuePosition: payload.queuePosition || payload.QueuePosition || 1,
          estimatedWaitSeconds: payload.estimatedWaitSeconds || payload.EstimatedWaitSeconds || 45,
          title: "Holding in Queue",
          subtitle: payload.message || "All agents are busy. Please hold...",
        }));
        callSessionRef.current.setCallState(CALL_STATES.queued);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callAccepted, async (payload = {}) => {
        const callId = getPayloadCallId(payload) || activeCallIdRef.current;
        if (!callId) return;
        const acceptedConnectionId = getPayloadValue(payload, "acceptedConnectionId") || getPayloadValue(payload, "fromConnectionId");
        const currentConnectionId = connection?.connectionId;
        if (acceptedConnectionId && currentConnectionId && String(acceptedConnectionId) === String(currentConnectionId)) {
          // This device accepted the call
          return;
        }
        activeCallIdRef.current = callId;
        callSessionRef.current.setCallInfo((current) => ({ ...(current || {}), ...payload, callId, isTransferring: false }));
        callSessionRef.current.setCallState(CALL_STATES.connected);
        await callSessionRef.current.createOffer(callId).catch(() => {});
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callTransferring, (payload = {}) => {
        const callId = getPayloadCallId(payload) || activeCallIdRef.current;
        if (!callId) return;
        const targetDeptName = payload.targetDepartmentName || payload.targetDepartment || "Department";
        const message = payload.message || `Transferring to ${targetDeptName}...`;
        callSessionRef.current.setCallInfo((current) => ({
          ...(current || {}),
          ...payload,
          callId,
          isTransferring: true,
          title: "Transferring call",
          subtitle: message,
        }));
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callTransferred, (payload = {}) => {
        console.log("[CallEngine] Call transferred successfully:", payload);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callSignal, (payload = {}) => {
        if (!isSameCall(payload, activeCallIdRef.current)) return;
        callSessionRef.current.handleSignal(payload);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callDeclined, (payload = {}) => {
        if (activeCallIdRef.current && !isSameCall(payload, activeCallIdRef.current)) return;
        const reason = payload.reason || payload.Reason;
        if (reason === "answered_elsewhere") return;
        callSessionRef.current.handleCallEnded(payload);
        activeCallIdRef.current = null;
        setActiveCall(null);
        setIncomingCall(null);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callEnded, (payload = {}) => {
        if (activeCallIdRef.current && !isSameCall(payload, activeCallIdRef.current)) return;
        const reason = payload.reason || payload.Reason;
        if (reason === "answered_elsewhere") return;
        callSessionRef.current.handleCallEnded(payload);
        activeCallIdRef.current = null;
        setActiveCall(null);
        setIncomingCall(null);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callUnavailable, (payload = {}) => {
        failCall(getPayloadMessage(payload, "The target is not available."), payload);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callBusy, (payload = {}) => {
        failCall(getPayloadMessage(payload, "The target is already on a call."), payload);
      }),
      registerHubHandler(hubClient, connection, CALL_HUB_EVENTS.callError, (payload = {}) => {
        failCall(getPayloadMessage(payload), payload);
      }),
      subscribeConnectionLifecycle(connection, "reconnecting", () => {
        setRegistrationState("reconnecting");
      }),
      subscribeConnectionLifecycle(connection, "reconnected", () => {
        if (!autoRegister) {
          setRegistrationState("registered");
          return;
        }
        registerClient().catch((registrationError) => {
          setRegistrationState("failed");
          failCall(registrationError?.message || "Unable to restore registration.", registrationError);
        });
      }),
      subscribeConnectionLifecycle(connection, "close", () => {
        setRegistrationState("disconnected");
      }),
    ];

    return () => {
      mounted = false;
      cleanup.forEach((dispose) => dispose());
    };
  }, [autoRegister, connection, failCall, hubClient, identity, presentIncomingCall, registerClient]);

  const cleanup = useCallback(() => {
    callSession.resetCall();
    callSession.setCallState(CALL_STATES.idle);
    callSession.setCallInfo(null);
    activeCallIdRef.current = null;
    setActiveCall(null);
    setIncomingCall(null);
  }, [callSession]);

  const acceptCall = useCallback(async (explicitCallId) => {
    const targetId = explicitCallId || activeCallIdRef.current || incomingCall?.callId || activeCall?.callId;
    try {
      const { unlockAudioPlayback } = await import("../realtime/callWebRtc.js");
      unlockAudioPlayback();
    } catch {}
    await callSessionRef.current.acceptCall(targetId);
  }, [activeCall?.callId, incomingCall?.callId]);

  const declineCall = useCallback(async (explicitCallId) => {
    const targetId = explicitCallId || activeCallIdRef.current || incomingCall?.callId || activeCall?.callId;
    await callSessionRef.current.declineCall(targetId);
    activeCallIdRef.current = null;
    setActiveCall(null);
    setIncomingCall(null);
  }, [activeCall?.callId, incomingCall?.callId]);

  const endCall = useCallback(async (explicitCallId) => {
    const targetId = explicitCallId || activeCallIdRef.current || incomingCall?.callId || activeCall?.callId;
    await callSessionRef.current.endCall(targetId);
    activeCallIdRef.current = null;
    setActiveCall(null);
    setIncomingCall(null);
  }, [activeCall?.callId, incomingCall?.callId]);

  const startCall = useCallback(async (target) => {
    if (!target) return null;
    try {
      const { unlockAudioPlayback } = await import("../realtime/callWebRtc.js");
      unlockAudioPlayback();
    } catch {}
    setError(null);
    const nextCall = {
      target,
      title: target.targetLabel || target.displayName || target.label || "Calling",
      subtitle: target.subtitle || "",
    };
    setActiveCall(nextCall);
    callSessionRef.current.setCallInfo(nextCall);
    callSessionRef.current.setCallState(CALL_STATES.calling);

    try {
      await startHubClient(hubClient);
      const result = await invokeHubClient(hubClient, connection, CALL_HUB_METHODS.startCall, {
        identity,
        target,
      });
      const callId = getPayloadCallId(result);
      if (callId) {
        activeCallIdRef.current = callId;
        setActiveCall((current) => ({ ...(current || {}), ...result, callId }));
        callSessionRef.current.setCallInfo((current) => ({ ...(current || {}), ...result, callId }));
        callSessionRef.current.setCallState(CALL_STATES.calling);
        callSessionRef.current.connectLiveKit(callId).catch(() => {});
      }
      return result;
    } catch (startError) {
      failCall(startError?.message || "Unable to start call.", startError);
      return null;
    }
  }, [connection, failCall, hubClient, identity]);

  const openIncomingCall = useCallback(async (callId) => {
    if (!connection || !callId) return null;

    try {
      await startHubClient(hubClient);
      await registerClient();
      const result = await invokeHubClient(hubClient, connection, CALL_HUB_METHODS.openIncomingCall, { callId });
      const status = String(getPayloadValue(result, "status") || "").toLowerCase();
      const call = getPayloadValue(result, "call") || result;
      if (status === "ringing" || !status) {
        presentIncomingCall(call, { allowExisting: true });
      }
      return result;
    } catch (openError) {
      failCall(openError?.message || "Unable to open incoming call.", openError);
      return null;
    }
  }, [connection, failCall, hubClient, presentIncomingCall, registerClient]);

  const transferCall = useCallback(async (targetDepartment, targetDepartmentName) => {
    try {
      return await callSessionRef.current.transferCall(targetDepartment, targetDepartmentName);
    } catch (transferError) {
      failCall(transferError?.message || "Unable to transfer call.", transferError);
      return null;
    }
  }, [failCall]);

  const holdCall = useCallback(async (nextHeld) => {
    return await callSessionRef.current.holdCall(nextHeld);
  }, []);

  return {
    registrationState,
    callState: callSession.callState,
    callInfo: callSession.callInfo || activeCall || incomingCall,
    activeCall,
    incomingCall,
    isHeld: callSession.isHeld,
    isRemoteHeld: callSession.isRemoteHeld,
    isTransferring: callSession.isTransferring,
    transferringDepartment: callSession.transferringDepartment,
    callDuration: callSession.callDuration,
    acceptCall,
    declineCall,
    endCall,
    startCall,
    holdCall,
    transferCall,
    cancelCall: () => endCall(),
    toggleMute: callSession.toggleMute,
    openIncomingCall,
    isMuted: callSession.muted,
    muted: callSession.muted,
    error,
    cleanup,
  };
};
